/* eslint-disable max-lines */
import { showSignUpModalAtom, streamingChatIdsAtom } from '@/atoms/chatAtoms';
import { chatSidebarOpenAtom } from '@/atoms/chatSidebarAtoms';
import { pendingNavHrefAtom } from '@/atoms/sideNavAtoms';
import { AnimatedChatTitle } from '@/components/chat/components/AnimatedChatTitle';
import { useChatHasDraft } from '@/components/chat/hooks/useChatInputDraft';
import { SidebarNavRowStatusIcon } from '@/components/layout/Navigation/sidebar/sidebar-nav-row-status';
import { resolveChatSidebarNavRowStatus } from '@/components/layout/Navigation/sidebar/sidebar-nav-row-status.resolve';
import { useEvidenceAnalytics } from '@/hooks/useEvidenceAnalytics';
import { trackSidebarProjectSelected } from '@/models/analytics/features/work';
import {
  IS_EVIDENCE_LIBRARY_ENABLED,
  IS_INSET_DESIGN_ENABLED,
  IS_SIDEBAR_PANEL_ENABLED,
} from '@/features/flags/legacy-flags';
import {
  useIsProjectsEnabled,
  useIsStandaloneCalculatorsEnabled,
  useIsNangoIntegrationsEnabled,
  useIsUnauthChatSidebarSignupCardLayoutEnabled,
  useIsWorkflowsEnabled,
} from '@/features/flags';
import { NANGO_SETTINGS_PATH } from '@heidi/integrations/connected-apps/contracts';
import {
  useIsCpdQueryEnabled,
  useIsCpdSidebarShown,
  useIsCpdUsUser,
} from '@/hooks/useIsCpdSurfaceEnabled';
import { useCPDTerm } from '@heidi/evidence';
import { useQueryCPDStats } from '@heidi/evidence/data';
import { useRenameInputFocus } from '@/hooks/useRenameInputFocus';
import { Button } from '@heidi/design-system/button';
import { Checkbox } from '@heidi/design-system/checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@heidi/design-system/collapsible';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@heidi/design-system/context-menu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@heidi/design-system/dropdown-menu';
import { HeidiLogoMark } from '@heidi/design-system/heidi-logo-mark';
import { Input } from '@heidi/design-system/input';
import { SearchInput } from '@heidi/design-system/search-input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import {
  TypographyCaption,
  TypographyH2,
  TypographyP2,
} from '@heidi/design-system/typography';
import { useIsRouteActive } from '@/components/layout/Navigation/hooks/useIsRouteActive';
import { SignUpPromptModalChat } from '@/components/modal/sign-up-prompt-modal-chat';
import { Loader } from '@heidi/snowflake-ui/loader';
import { useMutateBulkDeleteChats } from '@/hooks/mutation/useMutateBulkDeleteChats';
import { SavedChatsSidebarSection } from '@/components/chat/components/SavedChatsSidebarSection';
import type { ChatSidebarWorkBindings } from '@/components/layout/Navigation/hooks/useChatSidebarWorkBindings';
import { useRenameChat } from '@/components/chat/hooks/useRenameChat';
import { useInfiniteQueryChats } from '@/hooks/query/useInfiniteQueryChats';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { useFilteredSavedChats, useSavedChats } from '@/hooks/useSavedChats';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { cn } from '@/lib/cn';
import type { ChatListResponse } from '@heidi/session/contracts';
import type { ChatListItem } from '@/types';
import { getDefaultStore, useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  Calculator,
  ChevronDown,
  GraduationCap,
  LibraryBig,
  Microscope,
  MoreHorizontal,
  PanelRightOpenIcon,
  Pencil,
  Pin,
  Plug,
  SquarePen,
  Trash2,
} from 'lucide-react';

import Link from 'next/link';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useChatSelection } from '../hooks/useChatSelection';
import { ChatSelectionBar } from './ChatSelectionBar';
import { UnauthenticatedChatSignupCard } from './UnauthenticatedChatSignupCard';
import { CPDDiscoverabilityCard } from './CPDDiscoverabilityCard';
import DeleteChatDialog from './DeleteChatDialog';
import { Routines } from '@heidi/design-system/icons';
import { startTracking, TrackingKey } from '@heidi/runtime/performance';

/**
 * Untranslated backend placeholder title for a chat with no name yet; must
 * match backend copy (see `deriveOptimisticChatTitle` in
 * `src/hooks/mutation/useMutateCreateChat.ts`, the source of truth). Trimmed,
 * case-insensitive so this stays aligned even if casing/whitespace varies.
 *
 * Duplicated from `src/utils/chatPlaceholderTitle.ts` rather than imported:
 * a Chat-quarantine file importing a new `src/utils/**` module grows the
 * chat-baseline K2 ratchet (outbound pairs into that zone), which is
 * shrink-only — see `scripts/chat-baseline/README.md`.
 */
const isBackendPlaceholderChatTitle = (title: string | null | undefined): boolean => {
  const trimmed = title?.trim();
  return !trimmed || trimmed.toLowerCase() === 'new chat';
};

interface ChatSidebarItemProps {
  chat: ChatListItem;
  section: 'recent' | 'saved';
  currentChatId?: string | null;
  isStreaming: boolean;
  needsAttention: boolean;
  isSaved: boolean;
  isAuthenticated: boolean;
  isSidebarPanelEnabled: boolean;
  isSelecting: boolean;
  isChatSelected: boolean;
  isEditing: boolean;
  editingTitle: string;
  onEditingTitleChange: (value: string) => void;
  onSelectChat: (chatId: string) => void;
  onStartRename: (chat: ChatListItem, section: 'recent' | 'saved') => void;
  onSubmitRename: (chatId: string) => void;
  onCancelRename: () => void;
  onDeleteChatClick: (e: React.MouseEvent, chatId: string) => void;
  onSaveChatClick: (e: React.MouseEvent, chatId: string) => void;
  onUnsaveChatClick: (e: React.MouseEvent, chatId: string, source: 'sidebar' | 'saved_section') => void;
  onToggleSelection: (e: React.MouseEvent, chatId: string) => void;
  onSavedSectionDelete: (chatId: string) => void;
  isWorkflowsEnabled: boolean;
  onMakeAsWorkflow: (chat: ChatListItem) => void;
  chatHref: ChatSidebarWorkBindings['getChatHref'];
  chatListVersion: ChatSidebarWorkBindings['getChatListVersion'];
}

function ChatSidebarItem({
  chat,
  section,
  currentChatId,
  isStreaming,
  needsAttention,
  isSaved,
  isAuthenticated,
  isSidebarPanelEnabled,
  isSelecting,
  isChatSelected,
  isEditing,
  editingTitle,
  onEditingTitleChange,
  onSelectChat,
  onStartRename,
  onSubmitRename,
  onCancelRename,
  onDeleteChatClick,
  onSaveChatClick,
  onUnsaveChatClick,
  onToggleSelection,
  onSavedSectionDelete,
  isWorkflowsEnabled,
  onMakeAsWorkflow,
  chatHref,
  chatListVersion,
}: ChatSidebarItemProps) {
  const intl = useIntl();
  const router = useRouter();
  const isSelected = chat.id === currentChatId;
  const isAwaitingTitle = isStreaming && isBackendPlaceholderChatTitle(chat.title);
  // Backend title is an untranslated literal used for match-on-default logic
  // elsewhere (see deriveOptimisticChatTitle in useMutateCreateChat.ts) — swap
  // it for the translated placeholder only at render time.
  const displayChatTitle =
    isBackendPlaceholderChatTitle(chat.title)
      ? intl.formatMessage({
          defaultMessage: 'New chat',
          id: 'ex6NWuIWQJ',
          description: 'Placeholder title shown for a chat that has not been named yet',
        })
      : chat.title;

  const [menuOpenState, setMenuOpenState] = useState<'dropdown' | 'context' | null>(null);
  const isMenuOpen = menuOpenState !== null;

  const { inputRef, armedRef } = useRenameInputFocus({
    isEditing,
    onSubmit: onSubmitRename,
    chatId: chat.id,
  });

  // Per-row dispatch: v2 (harness-backed) agent chats live at a
  // different detail URL than v1 legacy chats. Intercept here so every
  // surface that mounts ``ChatSidebarItem`` gets the same dispatch
  // without changing parent click contracts. v1 falls through to
  // ``onSelectChat`` (analytics / pending chat id).
  const chatVersion = chatListVersion(chat);
  const canMakeAsWorkflow = isWorkflowsEnabled && chatVersion === 2;
  const prefetchProjectChat = useCallback(() => {
    if (chatVersion !== 2) return;
    void router.prefetch(chatHref({ id: chat.id, version: 2 }));
  }, [chatVersion, chatHref, chat.id, router]);
  const handleRowActivate = useCallback(() => {
    if (isEditing) return;
    if (chatVersion === 2) {
      trackSidebarProjectSelected({
        project_id: chat.id,
        entry_point: 'sidebar',
      });
      void router.push(chatHref({ id: chat.id, version: 2 }));
      return;
    }
    onSelectChat(chat.id);
  }, [isEditing, chatVersion, router, chat.id, onSelectChat, chatHref]);

  const contextMenuSaveLabel = isSaved
    ? intl.formatMessage({
        defaultMessage: 'Unpin',
        id: 'G/qw1w4EwK',
        description: 'Chat sidebar row: unpin the chat',
      })
    : intl.formatMessage({
        defaultMessage: 'Pin',
        id: 'rwoj3YleIG',
        description: 'Chat sidebar row: pin the chat',
      });
  const renameLabel = intl.formatMessage({
    defaultMessage: 'Rename', id: 'e4UBAWWN5G',
    description: 'Chat sidebar row: rename the chat',
  });
  const moreActionsLabel = intl.formatMessage({
    defaultMessage: 'More actions', id: '4WkSlgOJXJ',
    description: 'Chat sidebar row: open the row actions menu',
  });
  const deleteLabel = intl.formatMessage({
    defaultMessage: 'Delete', id: 'c9rk3Nl4na',
    description: 'Chat sidebar row: delete the chat',
  });
  const handleContextSave = useCallback(
    (e: React.MouseEvent) => {
      if (isSaved) {
        onUnsaveChatClick(e, chat.id, section === 'saved' ? 'saved_section' : 'sidebar');
      } else {
        onSaveChatClick(e, chat.id);
      }
    },
    [isSaved, onUnsaveChatClick, onSaveChatClick, chat.id, section]
  );

  const hasDraft = useChatHasDraft(chat.id);
  const navRowStatus = resolveChatSidebarNavRowStatus({
    isStreaming,
    needsAttention,
    hasDraft,
    isCurrentChat: isSelected,
  });

  return (
    <ContextMenu
      key={chat.id}
      onOpenChange={(open) =>
        setMenuOpenState((prev) =>
          open ? 'context' : prev === 'context' ? null : prev
        )
      }
    >
      <ContextMenuTrigger asChild>
        <Button
          asChild
          variant={isSidebarPanelEnabled ? 'panel_ghost' : 'ghost'}
          size="sm"
          onClick={handleRowActivate}
          onPointerEnter={prefetchProjectChat}
          onFocus={prefetchProjectChat}
          data-active={isSidebarPanelEnabled ? isSelected : undefined}
          data-menu-open={isMenuOpen ? 'true' : undefined}
          className={cn(
            'group/chat-item relative w-full justify-start p-2 transition-none hover:shadow-none',
            isSelected && !isSidebarPanelEnabled && 'bg-background-secondary',
            // Override active background to prevent flash while dropdown/context menu is open.
            // The base button variant uses a different active colour which conflicts.
            isSidebarPanelEnabled
              ? 'active:!bg-background-secondary'
              : 'active:!bg-background-tertiary-hover',
            isMenuOpen && 'text-text-primary',
            isMenuOpen &&
              (isSidebarPanelEnabled
                ? 'bg-background-secondary'
                : 'bg-background-tertiary-hover')
          )}
          disableAutomaticTypographyClasses
        >
          <div
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (isEditing) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleRowActivate();
              }
            }}
            className="flex w-full items-center gap-1.5"
          >
            <SidebarNavRowStatusIcon status={navRowStatus} />
            {isEditing ? (
              <Input
                ref={inputRef}
                value={editingTitle}
                onChange={(e) => onEditingTitleChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (armedRef.current) {
                      onSubmitRename(chat.id);
                    }
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    onCancelRename();
                  }
                  e.stopPropagation();
                }}
                onClick={(e) => e.stopPropagation()}
                wrapperClassName="min-w-0 flex-1 border-0 bg-transparent px-0 focus-within:border-0"
                className="h-auto font-normal leading-normal tracking-normal"
                aria-label={renameLabel}
              />
            ) : (
              <AnimatedChatTitle title={displayChatTitle} />
            )}
            <div className="ml-auto flex shrink-0 items-center gap-1.5">
              {isAuthenticated && (
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        aria-label={contextMenuSaveLabel}
                        className={cn(
                          'shrink-0 text-text-secondary transition-all hover:text-text-primary focus-visible:w-auto focus-visible:opacity-100',
                          'w-0 overflow-hidden group-hover/chat-item:w-auto group-data-[menu-open=true]/chat-item:w-auto'
                        )}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isSaved) {
                            onUnsaveChatClick(
                              e,
                              chat.id,
                              section === 'saved' ? 'saved_section' : 'sidebar'
                            );
                          } else {
                            onSaveChatClick(e, chat.id);
                          }
                        }}
                      >
                        <Pin size={14} className={cn(isSaved && 'fill-current')} />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="text-xs">
                      {contextMenuSaveLabel}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {section === 'recent' && isSelecting ? (
                <button
                  type="button"
                  className="flex shrink-0 items-center justify-center"
                  onClick={(e) => onToggleSelection(e, chat.id)}
                  aria-label={`Select ${chat.title}`}
                >
                  <Checkbox checked={isChatSelected} tabIndex={-1} />
                </button>
              ) : (
                <DropdownMenu
                  open={menuOpenState === 'dropdown'}
                  onOpenChange={(open) =>
                    setMenuOpenState((prev) =>
                      open ? 'dropdown' : prev === 'dropdown' ? null : prev
                    )
                  }
                >
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      aria-label={moreActionsLabel}
                      className={cn(
                        'shrink-0 text-text-secondary transition-all hover:text-text-primary focus-visible:w-auto focus-visible:opacity-100',
                        isStreaming
                          ? 'w-auto opacity-0 group-hover/chat-item:opacity-100 group-data-[menu-open=true]/chat-item:opacity-100'
                          : 'w-0 overflow-hidden group-hover/chat-item:w-auto group-data-[menu-open=true]/chat-item:w-auto'
                      )}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreHorizontal size={14} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenuItem
                      disabled={isAwaitingTitle}
                      onSelect={(e) => {
                        e.preventDefault();
                        setMenuOpenState(null);
                        onStartRename(chat, section);
                      }}
                    >
                      <Pencil size={14} />
                      {renameLabel}
                    </DropdownMenuItem>
                    {canMakeAsWorkflow && (
                      <DropdownMenuItem
                        disabled={isAwaitingTitle}
                        onSelect={(e) => {
                          e.preventDefault();
                          setMenuOpenState(null);
                          onMakeAsWorkflow(chat);
                        }}
                      >
                        <Routines />
                        <FormattedMessage
                          defaultMessage="Create routine from chat"
                          id="IJKbU5z0+O"
                          description="Project chat overflow menu: turn chat into a routine"
                        />
                      </DropdownMenuItem>
                    )}
                    {!isSaved && (
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          if (section === 'recent') {
                            onDeleteChatClick(e, chat.id);
                          } else {
                            onSavedSectionDelete(chat.id);
                          }
                        }}
                        variant="destructive"
                      >
                        <Trash2 size={14} />
                        {deleteLabel}
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </Button>
      </ContextMenuTrigger>
      <ContextMenuContent>
        {isAuthenticated && (
          <ContextMenuItem onClick={handleContextSave}>
            <Pin className={cn(isSaved && 'fill-current')} />
            {contextMenuSaveLabel}
          </ContextMenuItem>
        )}
        <ContextMenuItem
          disabled={isAwaitingTitle}
          onSelect={() => {
            setMenuOpenState(null);
            onStartRename(chat, section);
          }}
        >
          <Pencil />
          {renameLabel}
        </ContextMenuItem>
        {canMakeAsWorkflow && (
          <ContextMenuItem
            disabled={isAwaitingTitle}
            onSelect={() => {
              setMenuOpenState(null);
              onMakeAsWorkflow(chat);
            }}
          >
            <Routines />
            <FormattedMessage
              defaultMessage="Create routine from chat"
              id="IJKbU5z0+O"
              description="Project chat overflow menu: turn chat into a routine"
            />
          </ContextMenuItem>
        )}
        {!isSaved && (
          <ContextMenuItem
            variant="destructive"
            onClick={(e) => {
              if (section === 'recent') {
                onDeleteChatClick(e, chat.id);
              } else {
                onSavedSectionDelete(chat.id);
              }
            }}
          >
            <Trash2 />
            {deleteLabel}
          </ContextMenuItem>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
}

interface ChatSidebarProps {
  onSelectChat: (chatId: string) => void;
  onNewChat?: () => void;
  currentChatId?: string | null;
  onDeleteChat?: (chatId: string) => void;
  /**
   * When true, bypasses the chatSidebarOpenAtom visibility check.
   * Used when embedding ChatSidebar in another container (e.g., SidebarPanel).
   */
  forceOpen?: boolean;
  /**
   * When true, renders only the content (search, links, list, CPD card) without
   * the outer aside wrapper and header. Used for embedding in SidebarPanel
   * which provides its own wrapper and PanelHeader.
   */
  embedded?: boolean;
  /**
   * Everything the sidebar needs from Heidi Work, resolved by the app shell —
   * see `useChatSidebarWorkBindings`. Every host passes it; the sidebar itself
   * knows nothing about Work, Routines or the inbox.
   */
  work: ChatSidebarWorkBindings;
}

// ``ProjectsSidebarSection`` and ``SIDEBAR_PROJECTS_LIMIT`` were
// removed when the Work-home rollout promoted the Projects entry to
// the top-level sidebar (see ``EvidenceNavItem`` flag-gated label /
// href). Recent agent chats now appear in the main chat list with
// per-row routing (see `getChatHref` on `@heidi/agents/contracts`), so
// the dedicated section is no longer reachable from any state.

export const ChatSidebar = ({
  onSelectChat,
  onNewChat,
  currentChatId,
  onDeleteChat,
  forceOpen = false,
  embedded = false,
  work,
}: ChatSidebarProps) => {
  const intl = useIntl();
  const router = useRouter();
  const isRouteActive = useIsRouteActive();
  const setPendingNavHref = useSetAtom(pendingNavHrefAtom);
  // Evidence entry is active on the Work home when Evidence mode is selected.
  const isEvidenceNavActive =
    !currentChatId &&
    work.isWorkHomeEvidenceMode &&
    isRouteActive('/scribe/chat/projects');
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useAtom(chatSidebarOpenAtom);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [recentSectionOpen, setRecentSectionOpen] = useState(true);
  const [showSignUpModal, setShowSignUpModal] = useAtom(showSignUpModalAtom);
  const { isAuthenticated } = useIsAuthenticated();
  const enableLibrary = IS_EVIDENCE_LIBRARY_ENABLED() && isAuthenticated;
  const enableCalculators = useIsStandaloneCalculatorsEnabled();
  const enableNangoConnectedApps = useIsNangoIntegrationsEnabled();
  const isUnauthChatSidebarSignupCardLayoutEnabled =
    useIsUnauthChatSidebarSignupCardLayoutEnabled();
  const isSidebarPanelEnabled = IS_SIDEBAR_PANEL_ENABLED();
  // Inset design renders the standalone sidebar as a floating card on the
  // sidebar chrome (matching the session-list / OtherSidebarPanels treatment),
  // instead of the flush full-height `bg-surface-accent` panel.
  const isInsetDesign = IS_INSET_DESIGN_ENABLED();
  // CPD lives in the Evidence sidebar only while Work is OFF. When Work
  // is on, the entry moves to the account menu (SideNavFooter), so these
  // sidebar entries are suppressed to avoid a duplicate entry point.
  const isWorkEnabled = useIsProjectsEnabled();
  const isWorkflowsEnabled = useIsWorkflowsEnabled();

  // Evidence-only (Work OFF) sidebar CPD entry points.
  const showSidebarCPD = useIsCpdSidebarShown(isAuthenticated);

  const isCpdQueryEnabled = useIsCpdQueryEnabled();
  const { data: cpdStats } = useQueryCPDStats({
    enabled: showSidebarCPD,
    isCpdQueryEnabled,
  });
  const { term: cpdTerm } = useCPDTerm(useIsCpdUsUser());
  const {
    trackNewChatClicked,
    trackDeleteChatClicked,
    trackChatSaved,
    trackChatUnsaved,
    trackPastSessionClicked,
    trackChatDeleted,
    trackSidebarClose,
  } = useEvidenceAnalytics();
  const { isChatSaved, saveChat, unsaveChat } = useSavedChats();
  const { visibleSavedChats, filteredSavedChats, savedChatsPending } =
    useFilteredSavedChats(searchQuery, isWorkEnabled);

  const {
    data: chatsData,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQueryChats({ query: debouncedSearchQuery, pageSize: 20 });
  const { mutateAsync: bulkDeleteChatsAsync, isPending: isBulkDeletingChats } =
    useMutateBulkDeleteChats();
  const isBulkDeleting = isBulkDeletingChats || work.isDeletingProject;
  const {
    editingTitle,
    setEditingTitle,
    handleStartRename,
    handleSubmitRename,
    handleCancelRename,
    isEditingChat,
  } = useRenameChat();
  const {
    selectedIds,
    selectedCount,
    isSelecting,
    toggleSelection,
    clearSelection,
    isSelected: isChatSelected,
  } = useChatSelection();
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [streamingChatIds] = useAtom(streamingChatIdsAtom);

  const handleSavedSectionDelete = useCallback(
    (chatId: string) => {
      clearSelection();
      toggleSelection(chatId);
      setShowBulkDeleteDialog(true);
    },
    [clearSelection, toggleSelection]
  );

  useEffect(() => {
    // Skip visibility handler when forceOpen is true (embedded mode)
    if (forceOpen) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isOpen, setIsOpen, forceOpen]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.trim());
    }, 300);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [searchQuery]);

  // Flatten pages, then hide harness rows when Work is off — those
  // detail URLs aren't openable without the Projects gate.
  // Backend search already filters the list when a query is present.
  const chats = useMemo<ChatListItem[]>(() => {
    const pages = (chatsData?.pages ?? []) as ChatListResponse[];
    return work.filterChatsForWorkAvailability(
      pages.flatMap((page) => page.chats),
      isWorkEnabled
    );
  }, [chatsData?.pages, isWorkEnabled, work]);

  // Per-row delete dispatch needs to know which backend owns each id:
  // v1 chats live in the chats collection (bulk-delete endpoint), v2
  // chats are projected from the projects collection and must be
  // deleted via ``DELETE /projects/:id`` instead — otherwise the chats
  // refetch re-materialises the row. Older API responses default to
  // v1 (matches ``getChatHref``).
  const chatVersionById = useMemo(() => {
    const map = new Map<string, 1 | 2>();
    for (const chat of [...chats, ...visibleSavedChats]) {
      const version = work.getChatListVersion(chat);
      if (map.get(chat.id) === 2) continue;
      map.set(chat.id, version);
    }
    return map;
  }, [chats, visibleSavedChats, work]);

  // Handle sidebar close. No-op during the Routines spotlight — collapsing
  // would paint the Work-home expand control for a frame before the tour
  // re-opens the panel.
  const handleSidebarClose = useCallback(() => {
    if (work.isSidebarLockedOpen) return;
    setIsOpen(false);
    trackSidebarClose();
  }, [work, setIsOpen, trackSidebarClose]);

  // Handle new chat click
  const handleNewChatClick = useCallback(() => {
    trackNewChatClicked('sidebar');
    startTracking({
      key: TrackingKey.EVIDENCE_NEW_CHAT,
      metadata: { entryPoint: 'sidebar' },
    });
    onNewChat?.();
    onSelectChat('');
  }, [trackNewChatClicked, onNewChat, onSelectChat]);

  // Handle chat selection (past session)
  const handleSelectChat = useCallback(
    (chatId: string) => {
      trackPastSessionClicked(chatId);
      startTracking({
        key: TrackingKey.EVIDENCE_CHAT_OPEN,
        metadata: {
          entryPoint: 'sidebar',
          selected_chat_id: chatId,
        },
      });
      onSelectChat(chatId);
    },
    [trackPastSessionClicked, onSelectChat]
  );

  // Handle delete chat click — enters selection mode on first click
  const handleDeleteChatClick = useCallback(
    (e: React.MouseEvent, chatId: string) => {
      e.stopPropagation();
      trackDeleteChatClicked(chatId);
      toggleSelection(chatId);
    },
    [trackDeleteChatClicked, toggleSelection]
  );

  const handleToggleSelection = useCallback(
    (e: React.MouseEvent, chatId: string) => {
      e.stopPropagation();
      toggleSelection(chatId);
    },
    [toggleSelection]
  );

  const handleMakeAsWorkflow = useCallback(
    (chat: ChatListItem) => {
      const version = chatVersionById.get(chat.id) ?? work.getChatListVersion(chat);
      if (version !== 2) return;

      work.makeAsWorkflow(chat.id);
    },
    [chatVersionById, work]
  );

  // Handle save chat click
  const handleSaveChatClick = useCallback(
    (e: React.MouseEvent, chatId: string) => {
      e.stopPropagation();
      saveChat(chatId);
      trackChatSaved(chatId, 'sidebar');
    },
    [saveChat, trackChatSaved]
  );

  // Confirm handler for the delete dialog. Splits the selected ids by
  // chat version and fans out to the right backend per row, so v2
  // project chats actually get deleted instead of bouncing back on the
  // next ``/v2/chat/chats`` refetch. v2 deletes are intentionally
  // sequential so each optimistic rollback snapshots the latest cache.
  const handleConfirmBulkDelete = useCallback(async () => {
    const ids = [...selectedIds];
    const v1Ids: string[] = [];
    const v2Ids: string[] = [];
    for (const id of ids) {
      // Default to v1 for ids we can't resolve locally (e.g. row was
      // unmounted before the dialog opened) — matches the existing
      // ``getChatHref`` default.
      if (chatVersionById.get(id) === 2) {
        v2Ids.push(id);
      } else {
        v1Ids.push(id);
      }
    }

    const deletedIds = new Set<string>();
    let hasFailure = false;

    if (v1Ids.length > 0) {
      try {
        const result = await bulkDeleteChatsAsync(v1Ids);
        for (const id of result.deleted) deletedIds.add(id);
        if (result.failed.length > 0) {
          hasFailure = true;
        }
      } catch {
        hasFailure = true;
      }
    }

    for (const id of v2Ids) {
      try {
        await work.deleteProject(id);
        deletedIds.add(id);
      } catch {
        hasFailure = true;
      }
    }

    if (currentChatId && deletedIds.has(currentChatId)) {
      onDeleteChat?.(currentChatId);
    }

    if (hasFailure) return;

    setShowBulkDeleteDialog(false);
    clearSelection();
  }, [
    selectedIds,
    chatVersionById,
    bulkDeleteChatsAsync,
    work,
    currentChatId,
    onDeleteChat,
    clearSelection,
  ]);

  // Handle unsave chat click
  const handleUnsaveChatClick = useCallback(
    (e: React.MouseEvent, chatId: string, source: 'sidebar' | 'saved_section') => {
      e.stopPropagation();
      unsaveChat(chatId);
      trackChatUnsaved(chatId, source);
    },
    [unsaveChat, trackChatUnsaved]
  );

  // Don't render on mobile (mobile uses the history drawer in the header instead)
  // forceOpen bypasses BOTH the atom check AND mobile check for embedded usage (e.g., SidebarPanel, embed page)
  if (!forceOpen && isMobile) {
    return null;
  }

  const isVisible = forceOpen || isOpen;

  const renderChatItem = (chat: ChatListItem, section: 'recent' | 'saved') => (
    <ChatSidebarItem
      chat={chat}
      section={section}
      currentChatId={currentChatId}
      isStreaming={streamingChatIds.has(chat.id)}
      needsAttention={work.attentionChatIds.has(chat.id)}
      isSaved={isChatSaved(chat.id)}
      isAuthenticated={isAuthenticated}
      isSidebarPanelEnabled={isSidebarPanelEnabled}
      isSelecting={isSelecting}
      isChatSelected={isChatSelected(chat.id)}
      isEditing={isEditingChat(chat.id, section)}
      editingTitle={editingTitle}
      onEditingTitleChange={setEditingTitle}
      onSelectChat={handleSelectChat}
      onStartRename={handleStartRename}
      onSubmitRename={handleSubmitRename}
      onCancelRename={handleCancelRename}
      onDeleteChatClick={handleDeleteChatClick}
      onSaveChatClick={handleSaveChatClick}
      onUnsaveChatClick={handleUnsaveChatClick}
      onToggleSelection={handleToggleSelection}
      onSavedSectionDelete={handleSavedSectionDelete}
      isWorkflowsEnabled={isWorkflowsEnabled}
      onMakeAsWorkflow={handleMakeAsWorkflow}
      chatHref={work.getChatHref}
      chatListVersion={work.getChatListVersion}
    />
  );

  const renderChatList = (chatList: ChatListItem[], section: 'recent' | 'saved') => (
    <div className="flex flex-col gap-0.5">
      {chatList.map((chat) => (
        <div key={chat.id}>{renderChatItem(chat, section)}</div>
      ))}
    </div>
  );

  const searchPlaceholderLabel = intl.formatMessage({
    defaultMessage: 'Search chats', id: 'tImd8nJZN6',
    description: 'Chat sidebar: search input placeholder',
  });
  const signUpToSearchLabel = intl.formatMessage({
    defaultMessage: 'Sign up to search chat history', id: '/a3KxmI5/Y',
    description: 'Chat sidebar: search input title shown to unauthenticated users',
  });
  const libraryNavLabel = intl.formatMessage({
    defaultMessage: 'Library', id: 'sYCJx4kF7C',
    description: 'Chat sidebar: link to the Library',
  });
  const calculatorsNavLabel = intl.formatMessage({
    defaultMessage: 'Calculators', id: 'zm3mDg92Uu',
    description: 'Chat sidebar: link to Calculators',
  });
  const loadingChatsLabel = intl.formatMessage({
    defaultMessage: 'Loading chats', id: 'ItRQ2iHQtl',
    description: 'Chat sidebar: chat list loading state',
  });
  const noChatsFoundLabel = intl.formatMessage({
    defaultMessage: 'No chats found', id: 'suqXUQ8nlV',
    description: 'Chat sidebar: no chats match the search query',
  });
  const noChatsYetLabel = intl.formatMessage({
    defaultMessage: 'No chats yet', id: '8PGXpupbgX',
    description: 'Chat sidebar: chat list is empty',
  });
  const signUpTitleLabel = intl.formatMessage({
    defaultMessage: 'Sign up to access Chat History', id: 'uresbFQTIq',
    description: 'Chat sidebar: sign-up prompt modal title',
  });
  const signUpSubtitleLabel = intl.formatMessage({
    defaultMessage: 'Sign up to save and access your chat history across devices', id: 'g4BxdHIRL1',
    description: 'Chat sidebar: sign-up prompt modal subtitle',
  });
  const heidiBrandLabel = intl.formatMessage({
    defaultMessage: 'Heidi', id: 'b+qX7gA7VN',
    description: 'Chat sidebar: Heidi brand name in the unauthenticated header',
  });
  const evidenceBrandLabel = intl.formatMessage({
    defaultMessage: 'Evidence', id: 'YtF1DTzCV8',
    description: 'Chat sidebar: Evidence product name in the unauthenticated header',
  });
  const closeSidebarLabel = intl.formatMessage({
    defaultMessage: 'Close sidebar', id: '9va2Bk4W8A',
    description: 'Chat sidebar: close button aria-label',
  });
  const newChatButtonLabel = intl.formatMessage({
    defaultMessage: 'New chat', id: '/n4w7mLDDS',
    description: 'Chat sidebar: button to start a new chat',
  });

  // Search input - used in both embedded and standalone modes
  const searchInput = (
    <div className={cn(!embedded && 'px-1')}>
      <SearchInput
        placeholder={searchPlaceholderLabel}
        clearAriaLabel={intl.formatMessage({ defaultMessage: 'Clear search', id: '4YJHutR26p' })}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        disabled={!isAuthenticated}
        className="h-8"
        title={!isAuthenticated ? signUpToSearchLabel : undefined}
        autoComplete="off"
      />
    </div>
  );

  // When the signup card is shown, the history scroller must not use flex-1 (it would grow and
  // leave a gap above the card). Cap its max height and add a flex-1 spacer below the card so
  // extra space sits under the card, not between the list and the card.
  const unauthenticatedWithSignupCard =
    !isAuthenticated && !isLoading && isUnauthChatSidebarSignupCardLayoutEnabled;

  // Content below search - shared between embedded and standalone modes
  const sidebarContent = (
    <>
      {/* Links Section */}
      <div className={cn('flex flex-col gap-0.5', !embedded && 'px-1')}>
        {/* Routines — opens the Work-home routines split view */}
        {isWorkEnabled &&
          isWorkflowsEnabled &&
          work.renderRoutinesNavEntry({
            isSidebarOpen: isOpen,
            onRequestOpenSidebar: () => setIsOpen(true),
            children: (
              <button
                type="button"
                data-testid="chat-sidebar-routines"
                onClick={work.openRoutines}
                className={cn(
                  'flex h-8 w-full items-center gap-2 rounded-md px-2 py-1 text-start',
                  isSidebarPanelEnabled
                    ? 'text-text-primary hover:bg-background-secondary hover:text-text-primary active:bg-background-secondary-pressed'
                    : 'hover:bg-background-secondary',
                  work.isRoutinesNavActive &&
                    (isSidebarPanelEnabled
                      ? 'bg-background-secondary text-text-primary'
                      : 'bg-background-secondary')
                )}
              >
                <Routines className="size-4 shrink-0 text-text-primary" />
                <TypographyP2 className="font-medium text-text-primary">
                  Routines
                </TypographyP2>
              </button>
            ),
          })}

        {/* Library */}
        {enableLibrary && (
          <Link
            href="/library"
            onClick={() => setPendingNavHref('/library')}
            className={cn(
              'flex h-8 items-center gap-2 rounded-md px-2 py-1',
              isSidebarPanelEnabled
                ? 'text-text-primary hover:bg-background-secondary hover:text-text-primary active:bg-background-secondary-pressed'
                : 'hover:bg-background-secondary',
              isRouteActive('/library') &&
                (isSidebarPanelEnabled
                  ? 'bg-background-secondary text-text-primary'
                  : 'bg-background-secondary')
            )}
          >
            <LibraryBig
              size={16}
              className="shrink-0 text-text-primary"
            />
            <TypographyP2
              className="font-medium text-text-primary"
            >
              {libraryNavLabel}
            </TypographyP2>
          </Link>
        )}

        {/* Calculators */}
        {enableCalculators && (
          <Link
            href="/scribe/calculators"
            onClick={() => setPendingNavHref('/scribe/calculators')}
            className={cn(
              'flex h-8 items-center gap-2 rounded-md px-2 py-1',
              isSidebarPanelEnabled
                ? 'text-text-primary hover:bg-background-secondary hover:text-text-primary active:bg-background-secondary-pressed'
                : 'hover:bg-background-secondary',
              isRouteActive('/scribe/calculators') &&
                (isSidebarPanelEnabled
                  ? 'bg-background-secondary text-text-primary'
                  : 'bg-background-secondary')
            )}
          >
            <Calculator
              size={16}
              className="shrink-0 text-text-primary"
            />
            <TypographyP2
              className="font-medium text-text-primary"
            >
              {calculatorsNavLabel}
            </TypographyP2>
          </Link>
        )}

        {/* Work-gated: with Work off, sessionless Evidence is already the chat home. */}
        {isWorkEnabled && (
          <button
            type="button"
            onClick={work.openEvidenceHome}
            className={cn(
              'flex h-8 w-full items-center gap-2 rounded-md px-2 py-1 text-start',
              isSidebarPanelEnabled
                ? 'text-text-primary hover:bg-background-secondary hover:text-text-primary active:bg-background-secondary-pressed'
                : 'hover:bg-background-secondary',
              isEvidenceNavActive &&
                (isSidebarPanelEnabled
                  ? 'bg-background-secondary text-text-primary'
                  : 'bg-background-secondary')
            )}
          >
            <Microscope size={16} className="shrink-0 text-text-primary" />
            <TypographyP2 className="font-medium text-text-primary">Evidence</TypographyP2>
          </button>
        )}

        {/* Connected apps (Nango) — Outlook/Gmail/Drive etc. */}
        {enableNangoConnectedApps && (
          <Link
            href={NANGO_SETTINGS_PATH}
            onClick={() => setPendingNavHref(NANGO_SETTINGS_PATH)}
            className={cn(
              'flex h-8 items-center gap-2 rounded-md px-2 py-1',
              isSidebarPanelEnabled
                ? 'text-text-primary hover:bg-background-secondary hover:text-text-primary active:bg-background-secondary-pressed'
                : 'hover:bg-background-secondary',
              isRouteActive(NANGO_SETTINGS_PATH) &&
                (isSidebarPanelEnabled
                  ? 'bg-background-secondary text-text-primary'
                  : 'bg-background-secondary')
            )}
            data-testid="work-sidebar-connected-apps"
          >
            <Plug size={16} className="shrink-0 text-text-primary" />
            <TypographyP2 className="font-medium text-text-primary">
              Connected apps
            </TypographyP2>
          </Link>
        )}

        {/* CPD (Evidence-only placement; moves to account menu when Work is on) */}
        {showSidebarCPD && (
          <Link
            href="/evidence/cpd"
            onClick={() => setPendingNavHref('/evidence/cpd')}
            className={cn(
              'flex h-8 items-center gap-2 rounded-md px-2 py-1',
              isSidebarPanelEnabled
                ? 'text-text-primary hover:bg-background-secondary hover:text-text-primary active:bg-background-secondary-pressed'
                : 'hover:bg-background-secondary',
              isRouteActive('/evidence/cpd') &&
                (isSidebarPanelEnabled
                  ? 'bg-background-secondary text-text-primary'
                  : 'bg-background-secondary')
            )}
          >
            <GraduationCap
              size={16}
              className="shrink-0 text-text-primary"
            />
            <TypographyP2
              className="font-medium text-text-primary"
            >
              {cpdTerm}
            </TypographyP2>
          </Link>
        )}

      </div>

      {/* Chat list scrolls; signup card is fixed below the scrollport (not inside the scroller) */}
      <div className="flex min-h-0 max-h-full w-full max-w-full flex-1 flex-col overflow-hidden">
        <div
          className={cn(
            'flex min-h-0 w-full min-w-0 flex-col overflow-y-auto',
            // Inset design matches the session-list's fade scrollbar
            // (thumb hidden at rest, fades in on hover); `scrollbar-fade`
            // already reserves a stable gutter. Non-inset keeps the
            // always-visible minimal thumb.
            isInsetDesign
              ? 'scrollbar-fade'
              : 'scrollbar-subtle scrollbar-gutter-stable',
            unauthenticatedWithSignupCard
              ? 'max-h-[calc(100%-13rem)] flex-[0_1_auto]'
              : 'flex-1',
            embedded ? 'py-1 pr-1' : 'p-1'
          )}
        >
          <div className="flex w-full max-w-full flex-col gap-2">
            {isLoading ? (
              <div className="flex items-center justify-center p-4">
                <TypographyP2 className="text-text-secondary">
                  {loadingChatsLabel}
                </TypographyP2>
              </div>
            ) : chats.length === 0 && filteredSavedChats.length === 0 ? (
              <div className="flex items-center justify-center p-4">
                <TypographyP2 className="text-text-secondary">
                  {searchQuery ? noChatsFoundLabel : noChatsYetLabel}
                </TypographyP2>
              </div>
            ) : (
              <>
                {/* Your routines: scheduled first, then on-demand/event. Hides when empty. */}
                {isAuthenticated &&
                  isWorkEnabled &&
                  isWorkflowsEnabled &&
                  work.renderRoutineSections({
                    isSidebarPanelEnabled,
                    currentChatId,
                  })}

                {isAuthenticated && (
                  <SavedChatsSidebarSection
                    isAuthenticated={isAuthenticated}
                    filteredSavedChats={filteredSavedChats}
                    savedChatsPending={savedChatsPending}
                  >
                    {(chat) => renderChatItem(chat, 'saved')}
                  </SavedChatsSidebarSection>
                )}

                {/* Recent section */}
                {chats.length > 0 && (
                  <Collapsible open={recentSectionOpen} onOpenChange={setRecentSectionOpen}>
                    <CollapsibleTrigger className="group/recent flex w-full items-center justify-between p-2 pb-1">
                      <TypographyCaption className="font-medium text-text-tertiary">
                        <FormattedMessage
                          defaultMessage="Recent"
                          id="S1E8szHNNk"
                          description="Chat sidebar section heading for recent chats"
                        />
                      </TypographyCaption>
                      <ChevronDown
                        size={14}
                        className={cn(
                          'text-text-tertiary opacity-0 transition-[transform,opacity] duration-200 ease-out group-hover/recent:opacity-100',
                          recentSectionOpen && '-rotate-180'
                        )}
                      />
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <ChatSelectionBar
                        selectedCount={selectedCount}
                        onDelete={() => setShowBulkDeleteDialog(true)}
                        onClear={clearSelection}
                      />
                      {renderChatList(chats, 'recent')}
                      {hasNextPage && (
                        <div className="px-1">
                          <Button
                            variant={isSidebarPanelEnabled ? 'panel_ghost' : 'ghost'}
                            size="sm"
                            className="w-full justify-start px-2"
                            onClick={() => fetchNextPage()}
                            disabled={isFetchingNextPage}
                          >
                            {isFetchingNextPage ? (
                              <Loader size={14} strokeWidth={2} />
                            ) : (
                              <FormattedMessage
                                defaultMessage="Load more" id="0j79mapOmS"
                                description="Chat sidebar: load more chats in the recent section"
                              />
                            )}
                          </Button>
                        </div>
                      )}
                    </CollapsibleContent>
                  </Collapsible>
                )}
              </>
            )}
          </div>
        </div>
        {unauthenticatedWithSignupCard && (
          <>
            <div className={cn('shrink-0 pt-2', !embedded && 'px-1')}>
              <UnauthenticatedChatSignupCard />
            </div>
            <div className="min-h-0 min-w-0 flex-1" aria-hidden />
          </>
        )}
      </div>

      {/* CPD Activity Card - Expandable on hover (Evidence-only placement) */}
      {showSidebarCPD && (
        <CPDDiscoverabilityCard
          router={router}
          sourcesCount={cpdStats?.total_sources ?? 0}
          // Inset wraps the card in a `p-2` surface that already supplies the
          // bottom inset; drop the default `mb-2` to avoid double-padding.
          className={isInsetDesign ? 'mb-0' : undefined}
        />
      )}
    </>
  );

  // Embedded mode: render content only (for SidebarPanel)
  if (embedded) {
    return (
      <>
        <div className="flex h-full flex-col gap-2 overflow-hidden">
          {searchInput}
          {sidebarContent}
        </div>

        <DeleteChatDialog
          open={showBulkDeleteDialog}
          isDeleting={isBulkDeleting}
          count={selectedCount}
          onCancel={() => setShowBulkDeleteDialog(false)}
          onConfirm={handleConfirmBulkDelete}
        />

        <SignUpPromptModalChat
          isOpen={showSignUpModal}
          onClose={() => setShowSignUpModal(false)}
          title={signUpTitleLabel}
          subtitle={signUpSubtitleLabel}
        />
      </>
    );
  }

  // Standalone mode: full sidebar with wrapper and header
  const standaloneBody = (
    <>
      {/* Header + Search wrapper - matches main branch DOM structure */}
      <div className="space-y-2">
        {!isAuthenticated && (
            <div className="mb-4 flex flex-row items-center justify-between pl-2.5">
              <div className="flex flex-row items-center">
                <HeidiLogoMark className="me-2 size-4" />
                <TypographyH2 className="mr-1 text-[18px] font-medium text-text-primary">
                  {heidiBrandLabel}
                </TypographyH2>
                <TypographyH2 className="text-[18px] text-text-secondary">
                  {evidenceBrandLabel}
                </TypographyH2>
              </div>
              {/* Hide close button when forceOpen (embedded mode) */}
              {!forceOpen && (
                <Button
                  variant="ghost"
                  size="md"
                  iconOnly
                  onClick={handleSidebarClose}
                  disabled={work.isSidebarLockedOpen}
                  aria-label={closeSidebarLabel}
                >
                  <PanelRightOpenIcon size={16} />
                </Button>
              )}
            </div>
          )}
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="md" onClick={handleNewChatClick} data-testid="new-chat-button">
              <SquarePen size={16} />
              <span>{newChatButtonLabel}</span>
            </Button>

            {!forceOpen && isAuthenticated && (
              <Button
                variant="ghost"
                size="md"
                iconOnly
                onClick={handleSidebarClose}
                disabled={work.isSidebarLockedOpen}
                aria-label={closeSidebarLabel}
              >
                <PanelRightOpenIcon size={16} />
              </Button>
            )}
          </div>
        </div>

      {isAuthenticated && searchInput}

      {sidebarContent}
    </>
  );

  return (
    <>
      <aside
        aria-hidden={!isVisible}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore - inert is a valid HTML attribute but not in React types yet
        inert={!isVisible ? '' : undefined}
        className={cn(
          'flex h-full shrink-0 flex-col',
          // Non-inset paints its surface + child gap directly on the aside.
          // Inset turns the aside into a transparent gutter and floats the body
          // in a rounded `bg-surface` card — matching the session-list card and
          // the sibling panels in OtherSidebarPanels.
          //
          // Width is load-bearing for layout (the aside is a flex sibling of the
          // main content, which reclaims the freed space), so the reveal must
          // animate `width` — a transform can't release flex space. Inset keeps
          // its `py-3` gutter constant and adds the inline-end gap via `me-3`
          // (margin, outside the card box) so only width + margin animate; the
          // inner card is pinned to the open width so its content lays out once
          // and slides cleanly under `overflow-x-hidden` instead of reflowing.
          // ease-out + 300ms follows .ai-context/design-system/animations.md.
          isInsetDesign
            ? 'overflow-x-hidden py-3 transition-[width,margin] duration-300 ease-out motion-reduce:transition-none'
            : 'gap-2 overflow-hidden transition-all duration-300 ease-in-out',
          isVisible
            ? isInsetDesign
              ? 'w-[320px] me-3'
              : 'w-[320px] border-r bg-surface-accent px-2 py-4'
            : isInsetDesign
              ? 'w-0'
              : 'w-0 border-r-0 px-0 py-0'
        )}
      >
        {isInsetDesign ? (
          <div className="flex h-full w-[320px] flex-col gap-2 overflow-hidden rounded-lg bg-surface-shell p-2">
            {/* `p-2` (not `py-4`): the inset gutter (`py-3` on the aside)
                already supplies the outer spacing, so a smaller card padding
                lifts the header to align with the nav-rail logo and matches
                the sibling inset cards in OtherSidebarPanels. */}
            {standaloneBody}
          </div>
        ) : (
          standaloneBody
        )}
      </aside>

      <DeleteChatDialog
        open={showBulkDeleteDialog}
        isDeleting={isBulkDeleting}
        count={selectedCount}
        onCancel={() => setShowBulkDeleteDialog(false)}
        onConfirm={handleConfirmBulkDelete}
      />

      {/* Signup modal for chat history access */}
      <SignUpPromptModalChat
        isOpen={showSignUpModal}
        onClose={() => setShowSignUpModal(false)}
        title={signUpTitleLabel}
        subtitle={signUpSubtitleLabel}
      />
    </>
  );
};
